// poiesis/src/widgets/display/projections.ts
// Declared display projections for composite attribute kinds (option A,
// CEO design 2026-08-19). A composite SUBTYPE declares named projections in
// its params ({"projections": {name: {strategy, ...}}}); this module is the
// closed CLIENT-SIDE vocabulary that executes them. Two registries scale it:
// field-kind formatters (day-of-week, hhmm-time, ...) and summary strategies
// (join-fields, collapse-ranges, duration-sum, count-distinct). A new
// composite type declares data; it never adds React.

export interface ProjectionSpec {
  strategy: string
  default?: boolean
  out_kind?: 'text' | 'number'
  unit?: string
  // strategy-specific fields (entries/axis/fields/join/from/to/...)
  [k: string]: unknown
}

export type ProjectionResult =
  | { text: string; kind: 'text' }
  | { text: string; kind: 'number'; value: number }
  | { text: string; kind: 'reference-by-key'; value: string; targetType: string; key: string }
  | null

/** Dotted-path getter ("open.day" → v.open.day). */
function at(obj: unknown, path: string): unknown {
  let cur: unknown = obj
  for (const seg of path.split('.')) {
    if (cur == null || typeof cur !== 'object') return undefined
    cur = (cur as Record<string, unknown>)[seg]
  }
  return cur
}

// ── Field-kind formatters (closed vocabulary; one entry per field kind) ──────
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] // Google: 0 = Sunday

/** SEN-81 — the stored day numbering stays Google's (0 = Sunday); a week is
 *  READ Monday-first (ISO 8601). This is the one declaration of that rotation:
 *  the collapse-ranges axis order below and the opening-hours editor's row
 *  order both read it. */
export const ISO_WEEK: readonly number[] = [1, 2, 3, 4, 5, 6, 0]

/** Axis kinds with an intrinsic order and a closed domain. An axis kind not
 *  listed sorts numerically and has no domain (nothing can be "absent"). */
interface AxisKind {
  order: (v: unknown) => number
  domain?: readonly unknown[]
}
const AXIS_KINDS: Record<string, AxisKind> = {
  'day-of-week': { order: (v) => ISO_WEEK.indexOf(Number(v)), domain: ISO_WEEK },
}
const NUMERIC_AXIS: AxisKind = { order: (v) => Number(v) }

function fmtDay(v: unknown): string {
  const n = Number(v)
  return Number.isInteger(n) && n >= 0 && n <= 6 ? DAY_NAMES[n] : String(v ?? '')
}

function fmtHHMM(v: unknown): string {
  const s = String(v ?? '')
  if (!/^\d{4}$/.test(s)) return s
  const h = s.slice(0, 2), m = s.slice(2)
  return m === '00' ? h : `${h}:${m}`
}

const FIELD_FORMATTERS: Record<string, (v: unknown) => string> = {
  'day-of-week': fmtDay,
  'hhmm-time': fmtHHMM,
  text: (v) => String(v ?? ''),
  number: (v) => String(v ?? ''),
  'country-code': (v) => String(v ?? '').toUpperCase(),
}

export function formatField(kind: string | undefined, v: unknown): string {
  return (FIELD_FORMATTERS[kind ?? 'text'] ?? FIELD_FORMATTERS.text)(v)
}

/** minutes since midnight for "HHMM"; NaN for junk. */
function hhmmMinutes(v: unknown): number {
  const s = String(v ?? '')
  if (!/^\d{4}$/.test(s)) return NaN
  return Number(s.slice(0, 2)) * 60 + Number(s.slice(2))
}

// ── Strategies ───────────────────────────────────────────────────────────────

function entriesOf(value: unknown, spec: ProjectionSpec): Record<string, unknown>[] {
  const key = typeof spec.entries === 'string' ? spec.entries : null
  const raw = key ? at(value, key) : value
  return Array.isArray(raw) ? (raw as Record<string, unknown>[]) : []
}

function joinFields(value: unknown, spec: ProjectionSpec): ProjectionResult {
  const fields = Array.isArray(spec.fields) ? (spec.fields as string[]) : []
  const parts = fields
    .map((f) => at(value, f))
    .filter((v) => v != null && String(v).trim() !== '')
    .map((v) => String(v))
  if (parts.length === 0) return null
  return { text: parts.join(typeof spec.join === 'string' ? spec.join : ' · '), kind: 'text' }
}

function fieldValue(value: unknown, spec: ProjectionSpec): ProjectionResult {
  const field = typeof spec.field === 'string' ? spec.field : ''
  const raw = field ? at(value, field) : undefined
  if (raw == null || String(raw).trim() === '') return null
  return { text: formatField(typeof spec.field_kind === 'string' ? spec.field_kind : undefined, raw), kind: 'text' }
}

function referenceByKey(value: unknown, spec: ProjectionSpec): ProjectionResult {
  const field = typeof spec.field === 'string' ? spec.field : ''
  const targetType = typeof spec.target_type === 'string' ? spec.target_type : ''
  const key = typeof spec.key === 'string' ? spec.key : ''
  const raw = field ? at(value, field) : undefined
  if (!targetType || !key || raw == null || String(raw).trim() === '') return null
  return { text: String(raw), kind: 'reference-by-key', value: String(raw), targetType, key }
}

function collapseRanges(value: unknown, spec: ProjectionSpec): ProjectionResult {
  const entries = entriesOf(value, spec)
  if (entries.length === 0) return null
  const axis = String(spec.axis ?? '')
  const equalOn = Array.isArray(spec.equal_on) ? (spec.equal_on as string[]) : []
  const axisKindName = typeof spec.axis_kind === 'string' ? spec.axis_kind : 'text'
  const axisKind = AXIS_KINDS[axisKindName] ?? NUMERIC_AXIS
  const valueFields = Array.isArray(spec.value_fields) ? (spec.value_fields as string[]) : equalOn
  const isTimeRange = spec.value_kind === 'hhmm-range' && valueFields.length >= 2
  const hoursText = (e: Record<string, unknown>) => {
    if (isTimeRange) {
      const from = at(e, valueFields[0])
      const to = at(e, valueFields[1])
      // A range that never ends, or ends where it began (the next day), is
      // the whole day. A close at "0000" after an open is midnight: "24".
      if (to == null || String(to) === String(from)) return '24h'
      return `${fmtHHMM(from)}–${String(to) === '0000' ? '24' : fmtHHMM(to)}`
    }
    return valueFields.map((f) => String(at(e, f) ?? '')).join('–')
  }

  // One GROUP per axis value: every entry on that value, in start order. Two
  // groups collapse only when their whole sets match — a split day (09–12,
  // 13–17) is one group, never two segments.
  type Group = { axis: unknown; sig: string; text: string }
  const groups = new Map<string, Group>()
  const openEnded = isTimeRange && entries.some((e) => at(e, valueFields[1]) == null)
  if (openEnded && axisKind.domain) {
    // Open with no close (Google's 24/7 shape) spans every axis value.
    for (const v of axisKind.domain) groups.set(String(v), { axis: v, sig: '#24h', text: '24h' })
  } else {
    const byAxis = new Map<string, Record<string, unknown>[]>()
    for (const e of entries) {
      const k = String(at(e, axis))
      byAxis.set(k, [...(byAxis.get(k) ?? []), e])
    }
    for (const [k, es] of byAxis) {
      const ordered = [...es].sort((a, b) =>
        String(at(a, valueFields[0]) ?? '').localeCompare(String(at(b, valueFields[0]) ?? '')))
      groups.set(k, {
        axis: at(ordered[0], axis),
        sig: ordered.map((e) => equalOn.map((f) => String(at(e, f) ?? '')).join('|')).join(';'),
        text: ordered.map(hoursText).join(', '),
      })
    }
    // A declared absent_text names the domain values no entry covers.
    if (typeof spec.absent_text === 'string' && axisKind.domain) {
      for (const v of axisKind.domain) {
        if (!groups.has(String(v))) groups.set(String(v), { axis: v, sig: '#absent', text: spec.absent_text })
      }
    }
  }

  const sorted = [...groups.values()].sort((a, b) => axisKind.order(a.axis) - axisKind.order(b.axis))
  type Seg = { from: Group; to: Group }
  const segs: Seg[] = []
  for (const g of sorted) {
    const prev = segs[segs.length - 1]
    const contiguous = prev && axisKind.order(g.axis) === axisKind.order(prev.to.axis) + 1
    if (prev && contiguous && g.sig === prev.from.sig) prev.to = g
    else segs.push({ from: g, to: g })
  }
  const rangeText = (s: Seg) => {
    const a = formatField(axisKindName, s.from.axis)
    const b = formatField(axisKindName, s.to.axis)
    return a === b ? a : `${a}–${b}`
  }
  return { text: segs.map((s) => `${rangeText(s)} ${s.from.text}`).join(' · '), kind: 'text' }
}

function durationSum(value: unknown, spec: ProjectionSpec): ProjectionResult {
  const entries = entriesOf(value, spec)
  if (entries.length === 0) return null
  let minutes = 0
  for (const e of entries) {
    const a = hhmmMinutes(at(e, String(spec.from ?? '')))
    const b = hhmmMinutes(at(e, String(spec.to ?? '')))
    if (Number.isNaN(a) || Number.isNaN(b)) continue
    minutes += b > a ? b - a : b + 1440 - a // overnight wraps; equal = a full day
  }
  const hours = Math.round((minutes / 60) * 10) / 10
  const unit = typeof spec.unit === 'string' ? spec.unit : ''
  return { text: `${hours}${unit ? ' ' + unit : ''}`, kind: 'number', value: hours }
}

function countDistinct(value: unknown, spec: ProjectionSpec): ProjectionResult {
  const entries = entriesOf(value, spec)
  if (entries.length === 0) return null
  const n = new Set(entries.map((e) => String(at(e, String(spec.field ?? '')) ?? ''))).size
  return { text: String(n), kind: 'number', value: n }
}

export const STRATEGIES: Record<string, (value: unknown, spec: ProjectionSpec) => ProjectionResult> = {
  'join-fields': joinFields,
  'field': fieldValue,
  'reference-by-key': referenceByKey,
  'collapse-ranges': collapseRanges,
  'duration-sum': durationSum,
  'count-distinct': countDistinct,
}

/** The backend serves its accepted strategy vocabulary beside declarations.
 * Keep the executable registry in exact lockstep when that list is present. */
export function projectionStrategiesMatch(displaySpec: unknown): boolean {
  const served = (displaySpec as { strategies?: unknown } | null)?.strategies
  if (!Array.isArray(served)) return true // older server
  const local = Object.keys(STRATEGIES).sort()
  const remote = served.filter((value): value is string => typeof value === 'string').sort()
  return local.length === remote.length && local.every((value, index) => value === remote[index])
}

export function runProjection(value: unknown, spec: ProjectionSpec): ProjectionResult {
  const impl = STRATEGIES[spec.strategy]
  if (!impl) return null // undeclared strategy → caller falls back truthfully
  try {
    return impl(value, spec)
  } catch {
    return null // junk data never crashes a cell
  }
}

/** The declaration's default projection (explicit `default: true`, else the first). */
export function defaultProjection(displaySpec: unknown): ProjectionSpec | null {
  const projections = (displaySpec as { projections?: Record<string, ProjectionSpec> } | null)?.projections
  if (!projections) return null
  const entries = Object.entries(projections)
  if (entries.length === 0) return null
  const dflt = entries.find(([, s]) => s.default === true)
  return (dflt ?? entries[0])[1]
}

/** Resolve a stored format to its declared projection. A format that names a
 * composite sub-field but no projection uses the generic field strategy. */
export function projectionFor(
  displaySpec: unknown,
  format?: string | null,
  subFieldKinds?: Record<string, string>,
): ProjectionSpec | null {
  if (!projectionStrategiesMatch(displaySpec)) return null
  const projections = (displaySpec as { projections?: Record<string, ProjectionSpec> } | null)?.projections
  if (format && projections) return projections[format] ?? {
    strategy: 'field',
    field: format,
    field_kind: subFieldKinds?.[format],
  }
  return defaultProjection(displaySpec)
}
